import {
  appointmentListResponseSchema,
  appointmentResponseSchema,
  type Appointment,
  type CreateAppointmentRequest,
  type ListAppointmentsQuery,
  type UpdateAppointmentRequest,
  type UpdateAppointmentStatusRequest,
} from '@emr/shared';

import { ApiError, apiRequest } from './client.ts';

/**
 * The scheduling surface, validated against `@emr/shared`'s own response schemas — matching
 * `api/auth.ts`'s own stated convention (see that file's header) — rather than a locally-invented
 * approximation. `server/src/scheduling/routes.ts` is the route file these calls are wired against;
 * every request/response shape below is the exact one that file parses or returns.
 */

export type { Appointment };

/** `GET /api/appointments?providerId=&from=&to=` — one provider's schedule for a range, or, when
 * `query.providerId` is omitted, every provider's schedule in the caller's own org for that range
 * (the "everyone's schedule" view, `BACKLOG.md` K2's follow-up). Both bounds are still required by
 * `listAppointmentsQuerySchema`, so there is no "everything" query to fall back to. */
export async function listAppointments(
  query: ListAppointmentsQuery,
  signal?: AbortSignal,
): Promise<readonly Appointment[]> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  if (query.providerId !== undefined) {
    params.set('providerId', query.providerId);
  }
  const response = await apiRequest(
    `/api/appointments?${params.toString()}`,
    appointmentListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.appointments;
}

/** `POST /api/appointments` — books an appointment. Throws `ApiError` with `code === 'conflict'`
 * when the provider already has an overlapping active appointment; see `appointmentErrorMessage`. */
export async function createAppointment(input: CreateAppointmentRequest): Promise<Appointment> {
  const response = await apiRequest('/api/appointments', appointmentResponseSchema, {
    method: 'POST',
    body: input,
  });
  return response.appointment;
}

/** `PATCH /api/appointments/:id` — reschedule, move to another provider, or edit type/notes. */
export async function updateAppointment(
  id: string,
  input: UpdateAppointmentRequest,
): Promise<Appointment> {
  const response = await apiRequest(`/api/appointments/${id}`, appointmentResponseSchema, {
    method: 'PATCH',
    body: input,
  });
  return response.appointment;
}

/** `POST /api/appointments/:id/status` — a status transition. The caller is responsible for only
 * offering a transition `canTransitionAppointmentStatus` (`@emr/shared`) permits; see
 * `schedule/statusActions.ts`, which is the one place this UI decides which buttons to show. */
export async function updateAppointmentStatus(
  id: string,
  status: UpdateAppointmentStatusRequest['status'],
): Promise<Appointment> {
  const response = await apiRequest(`/api/appointments/${id}/status`, appointmentResponseSchema, {
    method: 'POST',
    body: { status },
  });
  return response.appointment;
}

/**
 * The scheduling-specific error vocabulary (`SCHEDULING_ERROR_CODES`, `@emr/shared`), turned into a
 * message a front-desk or clinical user can act on — a booking conflict in particular must read as
 * "this slot is taken," not as a generic failure, per the task brief.
 *
 * `client.ts`'s own `messageForCode` stays generic on purpose (it has no scheduling vocabulary); this
 * is a thin layer on top of it for the codes that are specific to this domain, checked before falling
 * back to the generic mapping.
 */
export function appointmentErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : 'Something went wrong.';
  }
  switch (error.code) {
    case 'conflict':
      return 'This provider already has an appointment in that slot. Pick a different time.';
    case 'patient_not_found':
      return 'That patient could not be found in this practice.';
    case 'provider_not_found':
      return 'That provider is not a current member of this practice.';
    case 'invalid_status_transition':
      return 'That status change is not allowed from the appointment’s current status.';
    case 'not_found':
      return 'That appointment no longer exists.';
    case 'audit_unavailable':
      return 'The change could not be recorded and was not made. Try again in a moment.';
    default:
      return error.message;
  }
}
